import { terraContract } from '@terra/shared-authorization';
import {
  ApplyToAllTenantsResponseSchema,
  DeleteTenantFeatureFlagRulesResponseSchema,
  FlagConfigResponseSchema,
  ListFlagsResponseSchema,
  ListTargetingApplicationsQuerySchema,
  ListTargetingApplicationsResponseSchema,
  TenantFeatureFlagSchema,
  TenantFeatureFlagsResponseSchema,
  UpdateFlagConfigSchema,
  UpdateTenantFeatureFlagsSchema,
} from './feature-flags.schema';
import { FEATURE_FLAGS_PATH_NAME } from './feature-flags.constants';
import { z } from 'zod';

export const FeatureFlagsContract = terraContract
  .prefix(`/${FEATURE_FLAGS_PATH_NAME}`)
  .router({
    listFlags: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List feature flags',
        description:
          'Returns all feature flags with key, values, type, and targeting rules.',
        inputStructure: 'detailed',
      })
      .output(ListFlagsResponseSchema),
    listTargetingApplications: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/targeting/applications',
        summary: 'List applications for targeting rules',
        description:
          'Returns platform applications for feature flag targeting. Restricted to Terra users.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ListTargetingApplicationsQuerySchema }))
      .output(ListTargetingApplicationsResponseSchema),
    getFlagConfig: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/{flagKey}',
        summary: 'Get flag config and rules',
        description:
          'Returns default value and ordered targeting rules for a flag.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            flagKey: TenantFeatureFlagSchema,
          }),
        }),
      )
      .output(FlagConfigResponseSchema),
    updateFlagConfig: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'PUT',
        path: '/{flagKey}',
        summary: 'Update flag config and rules',
        description: 'Updates default value and/or rules for a flag.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            flagKey: TenantFeatureFlagSchema,
          }),
          body: UpdateFlagConfigSchema,
        }),
      )
      .output(FlagConfigResponseSchema),
    getTenantFeatureFlags: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/tenant/{tenantId}',
        summary: 'Get tenant feature flags',
        description:
          'Retrieves the feature flags for the specified tenant. Internal UI endpoint.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            tenantId: z.string().describe('Tenant identifier'),
          }),
        }),
      )
      .output(TenantFeatureFlagsResponseSchema),
    updateTenantFeatureFlags: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'PUT',
        path: '/tenant/{tenantId}',
        summary: 'Update tenant feature flags',
        description:
          'Updates feature flags for the specified tenant. Internal UI endpoint.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            tenantId: z.string().describe('Tenant identifier'),
          }),
          body: UpdateTenantFeatureFlagsSchema,
        }),
      )
      .output(TenantFeatureFlagsResponseSchema),
    deleteTenantFeatureFlagRules: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'DELETE',
        path: '/tenant/{tenantId}',
        summary: 'Delete tenant feature flag rules',
        description:
          'Deletes all targeting rules for the specified tenant. Internal UI endpoint.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            tenantId: z.string().describe('Tenant identifier'),
          }),
        }),
      )
      .output(DeleteTenantFeatureFlagRulesResponseSchema),
    applyToAllTenants: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'POST',
        path: '/apply-to-all-tenants',
        summary: 'Apply feature flags to all tenants',
        description:
          'Merges the given feature flags into every tenant. Existing per-tenant overrides for other flags are preserved. Internal UI endpoint.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: UpdateTenantFeatureFlagsSchema }))
      .output(ApplyToAllTenantsResponseSchema),
  });
